'use client'

import { RequestValidationTriggers } from '../../../../MyStichtingOpenHelpers/src/static'
import { useValidationOnFields, useAddRelatedFieldsToEventDetail } from '../../../../MyStichtingOpenHelpers/src/static'
import React, { type FC, type FormEventHandler, type FormHTMLAttributes, useRef } from 'react'

interface Props extends FormHTMLAttributes<HTMLFormElement> {
    id: string
    onSubmit: FormEventHandler<HTMLFormElement>
    action: (payload: FormData) => void
    validateFieldsOn?: RequestValidationTriggers
    groupsOfRelatedFields?: string[][]
}
/**
 * @description Form element for use with our useForm hook
 * All input elements related to this form, that should receive user input,
 * are set up for validation.
 *
 * best practice is to set up this from element in a client component, and set up the form content
 * in a server component. See Example A
 *
 * When using this component, there is no need to use the following components:
 * - ValidateFields
 * - RelatedFields
 *
 * There may still be edge cases where you may want to use those components. Read their
 * documentation for more info.
 *
 * Should be used in conjunction with useForm. For more information read the documentation on useForm.
 *
 * @param props.onSubmit - provide the onSubmit function returned from useForm
 * @param props.action - provide the formAction that is returned from useForm
 * @param props.validateFieldsOn - change default behavior of when to validated a field or field group.
 * Can be set to:
 * - change: validate on blur (but only if the input's value has actually changed)
 * - input: validate on every change (keystroke, check a checkbox, etc). This is debounced.
 * - submit: validate on submit. This action actually skips partial validation, and just
 *   validates the entire schema on submit
 * - never: never validate a field. This functionality is not yet written.
 * default behavior is to validate a field on 'change'. The entire form is always validated on submit.
 * @param groupsOfRelatedFields - provide arrays of input names that you want to validate together.
 * When there is cross-field validation in your form, use this property group those fields in
 * partial validation. When no groups are provided, every field is individually validated.
 * See Example B.
 *
 * @todo TODO: write functionality for validateFieldsOn: NEVER, when it is first needed.
 *
 * @fires REQUEST_PARTIAL_VALIDATION_EVENT
 * @listens REQUEST_PARTIAL_VALIDATION_EVENT
 *
 * @see {@link MyStichtingOpenHelpers/src/UseForm/hooks/UseForm.ts}
 * @see {@link MyStichtingOpenComponents/src/components/UseForm/FormUtilityComponents/ValidateFields}
 * @see {@link MyStichtingOpenComponents/src/components/UseForm/FormUtilityComponents/RelatedFields}
 *
 * @example Example A - best practice
 * Part 1 - setting up the form:
 * ```
 * const MyExampleForm = ({id, children}) => {
 *    const {onSubmit, formAction} = useForm(FORM_ID, action, initialState)
 *    return (
 *       <Form id={FORM_ID} onSubmit={onSubmit} action={formAction}>
 *          {children}
 *       </Form>
 *    )
 * }
 * ```
 *
 * Part 2 - adding ssr form content
 * ```
 * <MyExampleForm id={FORM_ID}>
 *
 *    <FormField name={INPUT_A} label="input A">
 *        <FormNumberInput name={INPUT_A} min={3} defaultValue={2} />
 *    </FormField>
 *    <ValidationMessages formId={FORM_ID} forFieldNames={INPUT_A} />
 *
 * </MyExampleForm>
 * ```
 *
 * @example Example B - setting up groups of related fields
 * In the example below:
 * - When input-a changes, both input-a and input-b are validated
 * - When input-e changes, both input-d and input-e are validated
 * - When input-c changes, only input-c is validated (notice that input-c does not appear
 *   in groupsOfRelatedFields)
 * ```
 * <Form ... groupsOfRelatedFields={[['input-a', 'input-b'],[input-d, input-e]]}>
 *    <input ... name="input-a" />
 *    <input ... name="input-b" />
 *    <input ... name="input-c" />
 *    <input ... name="input-d" />
 *    <input ... name="input-e" />
 * <Form>
 * ```
 *
 * */
export const Form: FC<Props> = ({
    id,
    onSubmit,
    action,
    validateFieldsOn,
    children,
    groupsOfRelatedFields,
    ...formAttributes
}) => {
    const formRef = useRef(null)
    useValidationOnFields(formRef, validateFieldsOn ?? RequestValidationTriggers.CHANGE)
    useAddRelatedFieldsToEventDetail(formRef, groupsOfRelatedFields)
    return (
        <form {...formAttributes} id={id} onSubmit={onSubmit} action={action} ref={formRef} noValidate>
            {children}
        </form>
    )
}
